import { Button, Divider, Grid, Typography } from "@mui/material";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import BaseBox from "../../base/BaseBox/baseBox";
import MailForm from "../../form/MailForm";
import TextFieldFormControl from "../../field/TextFieldFormControl";
import DateField from "../../field/DateField/DateField";
import AnswerRow from "./AnswerRow";
import LoadingButton from "@mui/lab/LoadingButton";
import ValidationTypography from "../../other/ValidationTypography/ValidationTypography";
import { isEmptyValue } from "../../../helpers/methods";

const AnswerForm = forwardRef(function AnswerForm(
    { surveyDetails, readOnly, onEditAnswer, onSendSurvey, isSendingSurvey, isSaving },
    ref
) {
    const { t } = useTranslation();

    const [unfillRequiredFields, setUnfillRequiredFields] = useState([]);
    const [locallyFilledAnswerIds, setLocallyFilledAnswerIds] = useState(() => new Set());

    useEffect(() => {
        setLocallyFilledAnswerIds(new Set());
    }, [surveyDetails]);

    const getAnswerScoreFieldId = (answerId) => `hrscore_${answerId}`;

    const checkIfRequiredFieldsAreFill = () => {
        let unfillFieldsTemp = [];
        for (const answer of surveyDetails?.answers ?? []) {
            const isLocallyFilled = locallyFilledAnswerIds.has(answer.id);
            if (!isLocallyFilled && isEmptyValue(answer.hrscore)) {
                unfillFieldsTemp.push(getAnswerScoreFieldId(answer.id));
            }
        }
        setUnfillRequiredFields(unfillFieldsTemp);
        return unfillFieldsTemp;
    };

    useImperativeHandle(ref, () => ({
        checkIfRequiredFieldsAreFill,
    }));

    const handleEditAnswer = (answerId, dataToSend) => {
        if (Object.prototype.hasOwnProperty.call(dataToSend, "hrscore")) {
            const fieldId = getAnswerScoreFieldId(answerId);
            const isFilled = !isEmptyValue(dataToSend.hrscore);
            setUnfillRequiredFields((prev) =>
                isFilled ? prev.filter((id) => id !== fieldId) : prev
            );
            setLocallyFilledAnswerIds((prev) => {
                const next = new Set(prev);
                if (isFilled) {
                    next.add(answerId);
                } else {
                    next.delete(answerId);
                }
                return next;
            });
        }
        onEditAnswer(answerId, dataToSend);
    };

    return (
        <Grid container spacing={1}>
            <Grid item sm={6} >
                <TextFieldFormControl
                    name="hrsurvey_name"
                    label={t('form.answer_form.hrsurvey_name')}
                    value={surveyDetails?.hrsurvey_name}
                    readOnly
                />
            </Grid>
            <Grid item sm={6} >
                <TextFieldFormControl
                    name="evaluated_person"
                    label={t('form.answer_form.evaluated_person')}
                    value={surveyDetails?.evaluated_person}
                    readOnly
                />
            </Grid>
            <Grid item sm={6} >
                <TextFieldFormControl
                    name="my_role"
                    label={t('form.answer_form.my_role')}
                    value={surveyDetails?.my_role}
                    readOnly
                />
            </Grid>
            <Grid item sm={6} >
                <DateField
                    name="hrsurvey_date_expired"
                    label={t('form.answer_form.hrsurvey_date_expired')}
                    value={surveyDetails?.hrsurvey_date_expired}
                    readOnly
                />
            </Grid>

            <Grid item xs={12} sx={{ marginTop: 2, marginBottom: 1 }}>
                <Divider />
            </Grid>

            {surveyDetails?.answers?.map((answer, index) => {
                return (
                    <Grid item sm={12} marginTop={"15px"} key={answer.id}>
                        <AnswerRow
                            answerData={answer}
                            scores={surveyDetails?.scores}
                            readOnly={readOnly}
                            onEditAnswer={handleEditAnswer}
                            isInvalid={unfillRequiredFields.includes(getAnswerScoreFieldId(answer.id))}
                        />
                    </Grid>

                )
            })
            }
            
            {readOnly && isEmptyValue(surveyDetails?.my_survey_closed) && !isSaving &&
                <Grid item xs={12}>
                    <ValidationTypography
                        label={t('form.answer_form.survey_not_yet_closed')}
                    />
                </Grid>
            }
            <Grid
                item
                xs={12}
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    paddingTop: 1,
                }}
            >
                {!readOnly &&
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={onSendSurvey}
                        loading={isSendingSurvey}
                    >
                        {t('form.answer_form.send_survey')}
                    </LoadingButton>}
            </Grid>
        </Grid>
    );
});

AnswerForm.propTypes = {
    mails: PropTypes.array,
    onEditMail: PropTypes.func,
    onAddMail: PropTypes.func,
    onRemoveMail: PropTypes.func,
    readOnly: PropTypes.bool
}


AnswerForm.defaultProps = {
    mails: [],
    readOnly: true
}

export default AnswerForm;
